/*
 * Goloka V3 header: mega-menu disclosure.
 * Mouse: open after a short rest (140ms), close after a grace period (260ms) so a
 * diagonal move from the trigger into the panel does not drop it.
 * Keyboard / touch: click or Enter/Space toggles; ArrowDown opens and moves into the panel;
 * Escape closes and returns focus to the trigger. Every item is a plain link, so this is a
 * disclosure pattern (buttons with aria-expanded), not an ARIA menu.
 */
if (!customElements.get('v3-mega-nav')) {
  class V3MegaNav extends HTMLElement {
    connectedCallback() {
      this.wrap = this.closest('.v3h-wrap');
      this.items = [...this.querySelectorAll('[data-v3h-item]')];
      this.plain = [...this.querySelectorAll('[data-v3h-plain]')];
      this.dim = this.wrap && this.wrap.querySelector('[data-v3h-dim]');
      this.current = null;
      this.hoverOpened = false;
      this.timer = null;
      this.desktop = window.matchMedia('(min-width: 990px)');

      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-v3h-trigger]');
        trigger.addEventListener('click', (e) => this.onClick(e, item));
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.open(item);
            const first = item.querySelector('[data-v3h-panel] a');
            if (first) first.focus();
          }
        });
        item.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.schedule(() => {
            this.open(item);
            this.hoverOpened = true;
          }, this.current ? 60 : 140);
        });
        item.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.schedule(() => this.close(), 260);
        });
      });
      // Plain links closing a neighbour's panel promptly.
      this.plain.forEach((li) => {
        li.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'mouse') this.schedule(() => this.close(), 120);
        });
      });

      this.onKey = (e) => {
        if (e.key !== 'Escape' || !this.current) return;
        const trigger = this.current.querySelector('[data-v3h-trigger]');
        const hadFocus = this.contains(document.activeElement);
        this.close();
        if (hadFocus) trigger.focus();
      };
      this.onDown = (e) => {
        if (this.current && !this.contains(e.target)) this.close();
      };
      this.onFocusOut = (e) => {
        if (this.current && e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
      };
      this.onMq = () => this.close();
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('pointerdown', this.onDown);
      this.addEventListener('focusout', this.onFocusOut);
      this.desktop.addEventListener('change', this.onMq);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('pointerdown', this.onDown);
      this.desktop.removeEventListener('change', this.onMq);
      clearTimeout(this.timer);
    }

    schedule(fn, delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(fn, delay);
    }

    onClick(e, item) {
      clearTimeout(this.timer);
      // A mouse click right after hover-open should not slam it shut again.
      if (e.detail > 0 && this.current === item && this.hoverOpened) {
        this.hoverOpened = false;
        return;
      }
      if (this.current === item) this.close();
      else this.open(item);
    }

    open(item) {
      clearTimeout(this.timer);
      if (this.current && this.current !== item) this.setState(this.current, false);
      this.current = item;
      this.hoverOpened = false;
      this.setState(item, true);
      if (this.wrap) this.wrap.classList.add('is-mega-open');
    }

    close() {
      clearTimeout(this.timer);
      if (this.current) this.setState(this.current, false);
      this.current = null;
      this.hoverOpened = false;
      if (this.wrap) this.wrap.classList.remove('is-mega-open');
    }

    setState(item, open) {
      item.classList.toggle('is-open', open);
      item.querySelector('[data-v3h-trigger]').setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }
  customElements.define('v3-mega-nav', V3MegaNav);
}
